/**
 * 랭킹 API 캐싱 유틸리티
 * 서버 호출을 최소화하여 성능을 향상시킵니다.
 */

class RankingCache {
  constructor() {
    this.cache = new Map();
    this.pendingRequests = new Map();
    this.CACHE_DURATION = 1 * 60 * 1000; // 1분
  }

  /**
   * 캐시 키 생성
   */
  getCacheKey(limit = 50) {
    return `rankings_${limit}`;
  }

  /**
   * 캐시된 데이터 조회
   */
  getCachedData(limit = 50) {
    const key = this.getCacheKey(limit);
    const cached = this.cache.get(key);
    
    if (!cached) return null;
    
    // 만료 시간 체크
    if (Date.now() > cached.expiry) {
      this.cache.delete(key);
      return null;
    }
    
    console.log('✅ 클라이언트 캐시에서 랭킹 반환', { limit, age: Date.now() - cached.timestamp });
    return cached.data;
  }

  /**
   * 캐시에 데이터 저장
   */
  setCachedData(data, limit = 50) {
    const key = this.getCacheKey(limit);
    const cached = {
      data,
      timestamp: Date.now(),
      expiry: Date.now() + this.CACHE_DURATION,
    };
    
    this.cache.set(key, cached);
    console.log('💾 클라이언트 캐시에 랭킹 저장', { limit, count: data.length });
  }

  /**
   * 캐시 무효화
   */
  invalidateCache(limit = null) {
    if (limit) {
      const key = this.getCacheKey(limit);
      this.cache.delete(key);
      console.log('🗑️ 랭킹 캐시 무효화', { limit });
    } else {
      this.cache.clear();
      console.log('🗑️ 모든 랭킹 캐시 무효화');
    }
  }

  /**
   * 랭킹 데이터 조회 (캐시 우선)
   */
  async getRankings(limit = 50, forceRefresh = false) {
    const key = this.getCacheKey(limit);
    
    // 1. 강제 새로고침이 아닌 경우 캐시 확인
    if (!forceRefresh) {
      const cached = this.getCachedData(limit);
      if (cached) return cached;
    }

    // 2. 진행 중인 요청 확인
    if (this.pendingRequests.has(key)) {
      console.log('⏳ 진행 중인 랭킹 요청 대기', { limit });
      return this.pendingRequests.get(key);
    }

    // 3. 새로운 API 호출
    console.log('🌐 서버에서 랭킹 데이터 조회', { limit, forceRefresh });
    
    const apiCall = this.fetchFromServer(limit);
    this.pendingRequests.set(key, apiCall);

    try {
      const result = await apiCall;
      
      // 캐시에 저장
      if (result.success && result.data) {
        this.setCachedData(result.data, limit);
      }
      
      return result.data;
    } catch (error) {
      console.error('❌ 랭킹 API 호출 실패', error);
      
      // 오류 시 기존 캐시 반환 (만료되었어도)
      const cached = this.cache.get(key);
      if (cached) {
        console.log('🔄 오류로 인한 기존 캐시 반환', { limit });
        return cached.data;
      }
      
      throw error;
    } finally {
      this.pendingRequests.delete(key);
    }
  }

  /**
   * 서버 API 호출
   */
  async fetchFromServer(limit = 50) {
    const response = await fetch(`/api/user/rankings?limit=${limit}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * 캐시 상태 조회
   */
  getCacheStatus() {
    const status = {};
    for (const [key, cached] of this.cache.entries()) {
      const age = Date.now() - cached.timestamp;
      const remaining = cached.expiry - Date.now();
      status[key] = {
        age: Math.round(age / 1000), // 초
        remaining: Math.round(remaining / 1000), // 초
        expired: remaining <= 0,
      };
    }
    return status;
  }
}

// 싱글톤 인스턴스
const rankingCache = new RankingCache();

// 사용 예시 함수들
export const getRankings = (limit = 50, forceRefresh = false) => {
  return rankingCache.getRankings(limit, forceRefresh);
};

export const refreshRankings = (limit = 50) => {
  return rankingCache.getRankings(limit, true);
};

export const invalidateRankingCache = (limit = null) => {
  rankingCache.invalidateCache(limit);
};

export const getRankingCacheStatus = () => {
  return rankingCache.getCacheStatus();
};

export default rankingCache;
